import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { api, onApiUnauthorized, setApiToken } from '@/api/client';
import { computePermissions, type Permission, type RoleGrant } from '@/api/permissions';

/**
 * The session as the server describes it (`GET /auth/me`), including the
 * permissions group membership confers and whether content is confined to a
 * granted folder closure (docs/user-groups.md).
 */
interface SessionResponse {
  organizationId: string | null;
  /**
   * M6 — the house mandates a second factor and this session has not presented
   * one, so the API will refuse everything but the enrollment routes. Re-read on
   * every `refreshGrants()`, which is what lets a mandate turned on mid-session
   * take effect at the next navigation rather than at the next login.
   */
  mfaSetupRequired?: boolean;
  grants: RoleGrant[];
  rolePermissions: Permission[];
  groupPermissions: Permission[];
  groupAccess: {
    groupIds: string[];
    canPublish: boolean;
    mediaAnchorIds: string[];
    playlistAnchorIds: string[];
    contentScoped: boolean;
  } | null;
}

/** One row of `GET /auth/organizations` — an org the caller may switch to. */
export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
}

interface OrganizationsResponse {
  organizations: OrganizationSummary[];
  activeOrganizationId: string | null;
}

const STORAGE_KEY = 'libretto42:auth';

interface LoginResponse {
  token: string;
  expiresAt: string;
  absoluteExpiresAt: string;
  organizationId: string | null;
  mfaSetupRequired?: boolean;
  user: {
    id: string;
    email: string;
    displayName: string;
    mfaEnabled: boolean;
    isPlatformRoot: boolean;
  };
}

interface PersistedState {
  token: string;
  user: LoginResponse['user'];
  organizationId: string | null;
  grants: RoleGrant[];
  groupPermissions?: Permission[];
  groupAccess?: SessionResponse['groupAccess'];
  mfaSetupRequired?: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null);
  const user = ref<LoginResponse['user'] | null>(null);
  const organizationId = ref<string | null>(null);
  const grants = ref<RoleGrant[]>([]);
  /** Conferred by group membership on top of the role bundle (UG3). */
  const groupPermissions = ref<Permission[]>([]);
  const groupAccess = ref<SessionResponse['groupAccess']>(null);
  /**
   * M6 — true while this session is confined to the enrollment flow. The router
   * reads it to divert every navigation to /security/setup, so it is persisted
   * alongside the token: a page reload must not land the user back in an app
   * whose every request is about to 403.
   */
  const mfaSetupRequired = ref(false);
  /**
   * Organizations the session may switch to (`GET /auth/organizations`).
   * `null` = not yet loaded — the UserMenu triggers the load lazily on first
   * open rather than login paying for it. Deliberately not persisted: it is
   * cheap to refetch and staleness across tabs would be confusing.
   */
  const organizations = ref<OrganizationSummary[] | null>(null);

  function loadFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as PersistedState;
      token.value = parsed.token;
      user.value = parsed.user;
      organizationId.value = parsed.organizationId;
      grants.value = parsed.grants ?? [];
      groupPermissions.value = parsed.groupPermissions ?? [];
      groupAccess.value = parsed.groupAccess ?? null;
      mfaSetupRequired.value = parsed.mfaSetupRequired ?? false;
      setApiToken(parsed.token);
    } catch {
      // Ignore corrupted storage
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  function persist(): void {
    if (!token.value || !user.value) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const state: PersistedState = {
      token: token.value,
      user: user.value,
      organizationId: organizationId.value,
      grants: grants.value,
      groupPermissions: groupPermissions.value,
      groupAccess: groupAccess.value,
      mfaSetupRequired: mfaSetupRequired.value,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  /**
   * Password sign-in. `totp` and `recoveryCode` are alternatives, not a pair:
   * the second is the printout someone reaches for when the phone with the
   * first on it is at home (M4).
   */
  async function login(
    email: string,
    password: string,
    second?: { totp?: string; recoveryCode?: string },
  ): Promise<void> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
      ...(second?.totp ? { totp: second.totp } : {}),
      ...(second?.recoveryCode ? { recoveryCode: second.recoveryCode } : {}),
    });
    adoptSession(response.data);
    // Load grants for the newly-active org
    await refreshGrants();
    persist();
  }

  /**
   * Adopt a session the server has already issued.
   *
   * Shared by password login and by the SSO handoff exchange, which return the
   * same shape for exactly this reason: two adoption paths would be two places
   * to forget `setApiToken`, and the one that forgot it would fail as a
   * mysterious 401 on the first real request rather than as anything legible.
   */
  function adoptSession(data: LoginResponse): void {
    token.value = data.token;
    user.value = data.user;
    organizationId.value = data.organizationId;
    mfaSetupRequired.value = data.mfaSetupRequired === true;
    setApiToken(data.token);
  }

  /**
   * Finish a Google/Microsoft sign-in by redeeming the one-shot handoff code the
   * callback put in the URL (S6). The code is good for sixty seconds and for one
   * use; what comes back is an ordinary session.
   */
  async function completeSsoSignIn(code: string): Promise<void> {
    const response = await api.post<LoginResponse>('/auth/sso/exchange', { code });
    adoptSession(response.data);
    await refreshGrants();
    persist();
  }

  /**
   * Called after a successful enrollment: the session has stepped up server-side
   * (`upgradeSessionAuthMethod`), so the local confinement flag must clear too or
   * the router keeps diverting a session the API is perfectly happy with.
   */
  function clearMfaSetupRequirement(): void {
    mfaSetupRequired.value = false;
    persist();
  }

  async function switchOrganization(targetOrgId: string): Promise<void> {
    await api.post('/auth/switch-organization', { organizationId: targetOrgId });
    organizationId.value = targetOrgId;
    await refreshGrants();
    persist();
  }

  /**
   * Load the session's own grants and content scope from `GET /auth/me`.
   *
   * This used to scan `/memberships` for the caller's own row, which only works
   * for a principal permitted to read the member list — a group-scoped user is
   * not (UG9), and would have ended up with no grants and an empty UI. The
   * session now describes itself, which also carries the permissions its group
   * grants confer.
   */
  async function refreshGrants(): Promise<void> {
    if (!user.value || !organizationId.value) {
      grants.value = [];
      groupPermissions.value = [];
      groupAccess.value = null;
      return;
    }
    try {
      const me = await api.get<SessionResponse>('/auth/me');
      grants.value = me.data.grants ?? [];
      groupPermissions.value = me.data.groupPermissions ?? [];
      groupAccess.value = me.data.groupAccess ?? null;
      // The server is authoritative on the mandate, and this is the only place
      // that hears about one switched on while the session was already open.
      mfaSetupRequired.value = me.data.mfaSetupRequired === true;
    } catch {
      // Non-fatal — keep what we had
    }
  }

  /**
   * Load the organizations this session may switch between. Errors propagate —
   * the caller decides whether a failure matters (the UserMenu ignores it and
   * simply renders no org section).
   */
  async function loadOrganizations(): Promise<void> {
    const response = await api.get<OrganizationsResponse>('/auth/organizations');
    organizations.value = response.data.organizations;
    // The server's view of the active org is authoritative; only sync a
    // concrete answer — a null here never wipes a locally-known org.
    if (
      response.data.activeOrganizationId !== null &&
      response.data.activeOrganizationId !== organizationId.value
    ) {
      organizationId.value = response.data.activeOrganizationId;
      // Grants/group scope are org-scoped: adopting the server's org without
      // them would pair the new org id with the old org's rows, and
      // hasPermission() would then compute the empty set (while stale group
      // permissions kept leaking through). Move the whole bundle together.
      await refreshGrants();
      persist();
    }
  }

  function logout(): void {
    token.value = null;
    user.value = null;
    organizationId.value = null;
    organizations.value = null;
    grants.value = [];
    groupPermissions.value = [];
    groupAccess.value = null;
    mfaSetupRequired.value = false;
    setApiToken(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  async function logoutFromServer(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore — local clear is authoritative
    } finally {
      logout();
    }
  }

  const isAuthenticated = computed(() => token.value !== null);

  /**
   * Compile effective permissions against the current active organization.
   * Workspace-scoped guards should call `hasPermission(permission, workspaceId)`.
   */
  function hasPermission(permission: Permission, workspaceId: string | null = null): boolean {
    if (user.value?.isPlatformRoot) return true;
    if (!organizationId.value) return false;
    const effective = computePermissions(grants.value, {
      organizationId: organizationId.value,
      workspaceId,
    });
    // Group grants are additive and org-wide in effect: which *rows* they reach
    // is decided server-side by the granted folder closure, so they widen what
    // the UI offers without regard to the workspace being asked about (UG3).
    return effective.has(permission) || groupPermissions.value.includes(permission);
  }

  /**
   * UG3/UG9 — is this session's content confined to granted folder subtrees?
   * True only for a user whose access comes from group grants alone; an admin
   * who also sits in a group is not scoped, and sees the full product.
   */
  const isContentScoped = computed(() => groupAccess.value?.contentScoped === true);

  /** UG8 — may this session publish directly, or only submit for approval? */
  const canPublishDirectly = computed(
    () => !isContentScoped.value || groupAccess.value?.canPublish === true,
  );

  // Wire into the API client's 401 handler
  onApiUnauthorized(() => {
    logout();
  });

  // Boot
  loadFromStorage();

  return {
    token,
    user,
    organizationId,
    grants,
    groupPermissions,
    groupAccess,
    organizations,
    loadOrganizations,
    isContentScoped,
    canPublishDirectly,
    isAuthenticated,
    mfaSetupRequired,
    login,
    completeSsoSignIn,
    clearMfaSetupRequirement,
    switchOrganization,
    refreshGrants,
    logout,
    logoutFromServer,
    hasPermission,
  };
});
